<template>
  <div class="mod-page sat-generic-page">
    <div class="mod-header">
      <div>
        <h1 class="mod-title">{{ def?.Nombre || 'Catálogo SAT' }}</h1>
        <p class="mod-subtitle">{{ def?.Grupo || 'SAT' }} · {{ def?.TablaEmpresa || '' }} · Empresa {{ empresaId || '-' }}</p>
      </div>
      <div class="mod-header-actions">
        <select v-model.number="selectedEmpresaId" class="mod-select sat-empresa-select" @change="onEmpresaChange">
          <option v-for="empresa in empresasActivas" :key="empresa.EmpresaId" :value="empresa.EmpresaId">
            {{ empresa.Nombre || empresa.Codigo || empresa.EmpresaId }}
          </option>
        </select>
        <button class="mod-btn mod-btn-outline" @click="$router.push('/catalogos/sat')">
          <i class="bi bi-arrow-left"></i>
          <span>Catálogos SAT</span>
        </button>
        <button class="mod-btn mod-btn-outline" :disabled="loading || syncing" @click="copyGlobal">
          <span v-if="syncing" class="mod-spinner"></span>
          <i v-else class="bi bi-arrow-repeat"></i>
          <span>{{ syncing ? 'Copiando...' : 'Copiar global' }}</span>
        </button>
        <button class="mod-btn mod-btn-primary" :disabled="!def" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nuevo</span>
        </button>
      </div>
    </div>

    <div class="mod-filters">
      <div class="mod-search">
        <i class="bi bi-search"></i>
        <input v-model="searchText" type="text" placeholder="Buscar por clave o descripción..." @keyup.enter="reloadFirstPage">
      </div>
      <div class="mod-filter-right">
        <select v-if="hasActive" v-model="statusFilter" class="mod-select" @change="reloadFirstPage">
          <option value="">Todos</option>
          <option value="activos">Activos</option>
        </select>
        <select v-model.number="pageSize" class="mod-select" @change="reloadFirstPage">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="200">200</option>
        </select>
        <button class="mod-btn mod-btn-outline" @click="reloadFirstPage">
          <i class="bi bi-search"></i>
          <span>Buscar</span>
        </button>
        <span class="mod-filter-count">{{ total }} registro{{ total !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <div class="mod-table-wrap">
      <table class="mod-table sat-generic-table">
        <thead>
          <tr>
            <th>Empresa</th>
            <th v-for="campo in visibleFields" :key="campo.Campo">{{ campo.Titulo }}</th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="visibleFields.length + 2" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="items.length === 0">
            <td :colspan="visibleFields.length + 2" class="mod-td-center mod-td-empty">
              <i class="bi bi-inbox"></i>
              <span>Sin registros</span>
            </td>
          </tr>
          <tr v-for="item in items" :key="rowKey(item)" class="mod-tr">
            <td>{{ empresaId }}</td>
            <td v-for="campo in visibleFields" :key="campo.Campo">
              <span v-if="campo.EsLlave" class="mod-badge">{{ value(item, campo.Campo) }}</span>
              <span v-else-if="campo.Tipo === 'bool'" :class="asBool(value(item, campo.Campo)) ? 'mod-status-active' : 'mod-status-inactive'">
                {{ asBool(value(item, campo.Campo)) ? 'Sí' : 'No' }}
              </span>
              <strong v-else-if="campo.EsNombre">{{ value(item, campo.Campo) || '-' }}</strong>
              <span v-else class="mod-td-muted">{{ value(item, campo.Campo) || '-' }}</span>
            </td>
            <td class="mod-td-actions">
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="mod-pagination">
      <button class="mod-page-btn" :disabled="page === 1" @click="goPage(1)"><i class="bi bi-chevron-double-left"></i></button>
      <button class="mod-page-btn" :disabled="page === 1" @click="goPage(page - 1)"><i class="bi bi-chevron-left"></i></button>
      <button v-for="p in visiblePages" :key="p" class="mod-page-btn" :class="{ 'mod-page-active': p === page }" @click="goPage(p)">{{ p }}</button>
      <button class="mod-page-btn" :disabled="page === totalPages" @click="goPage(page + 1)"><i class="bi bi-chevron-right"></i></button>
      <button class="mod-page-btn" :disabled="page === totalPages" @click="goPage(totalPages)"><i class="bi bi-chevron-double-right"></i></button>
      <span class="mod-page-info">Página {{ page }} de {{ totalPages }}</span>
    </div>

    <div v-if="showForm" class="mod-modal-backdrop">
      <div class="mod-modal sat-form-modal">
        <div class="mod-modal-header">
          <h2>{{ isEdit ? 'Editar registro' : 'Nuevo registro' }}</h2>
          <button class="mod-btn-icon" title="Cerrar" @click="closeForm"><i class="bi bi-x-lg"></i></button>
        </div>
        <form class="mod-form" @submit.prevent="save">
          <div class="sat-form-grid">
            <div v-for="campo in formFields" :key="campo.Campo" class="mod-field">
              <label>{{ campo.Titulo }}</label>
              <input
                v-if="campo.Tipo !== 'bool'"
                v-model="form[campo.Campo]"
                :type="campo.Tipo === 'date' ? 'date' : 'text'"
                class="mod-input"
                :disabled="isEdit && campo.EsLlave"
                :required="campo.EsLlave || campo.EsNombre"
              >
              <label v-else class="sat-check">
                <input v-model="form[campo.Campo]" type="checkbox">
                <span>Activo</span>
              </label>
            </div>
          </div>
          <div class="mod-modal-actions">
            <button type="button" class="mod-btn mod-btn-outline" @click="closeForm">Cancelar</button>
            <button type="submit" class="mod-btn mod-btn-primary" :disabled="saving">
              <span v-if="saving" class="mod-spinner"></span>
              <i v-else class="bi bi-check-lg"></i>
              <span>{{ saving ? 'Guardando...' : 'Guardar' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import satCatalogoService from './satcatalogo.service';
import authService from '@/services/auth';
import { showToast } from '@/shared/utils/toast';

export default {
  name: 'SatCatalogoGenericoView',
  data() {
    return {
      def: null,
      items: [],
      total: 0,
      page: 1,
      pageSize: 25,
      searchText: '',
      statusFilter: '',
      empresas: [],
      selectedEmpresa: null,
      selectedEmpresaId: 0,
      loading: false,
      saving: false,
      syncing: false,
      showForm: false,
      isEdit: false,
      form: {}
    };
  },
  computed: {
    catalogo() { return this.$route.params.catalogo; },
    empresaId() { return Number(this.selectedEmpresa?.EmpresaId || 0); },
    empresasActivas() { return this.empresas.filter(e => e.EsActivo === true || e.EsActivo == null); },
    fields() { return this.def?.Campos || []; },
    visibleFields() { return this.fields.filter(f => f.EsVisibleListado).slice(0, 8); },
    formFields() { return this.fields.filter(f => f.EsEditable !== false); },
    hasActive() { return this.fields.some(f => f.EsActivo); },
    totalPages() { return Math.max(1, Math.ceil(this.total / this.pageSize)); },
    visiblePages() {
      const pages = [];
      let start = Math.max(1, this.page - 2);
      let end = Math.min(this.totalPages, this.page + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(this.totalPages, start + 4);
        else start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    catalogo() { this.bootstrap(); }
  },
  created() {
    this.loadEmpresas();
    this.bootstrap();
  },
  methods: {
    loadEmpresas() {
      const userData = authService.getUserData();
      this.empresas = userData?._UsuarioMo?._EmpresaList || [];
      const selected = authService.getSelectedEmpresa();
      this.selectedEmpresa = selected || this.empresasActivas[0] || null;
      this.selectedEmpresaId = Number(this.selectedEmpresa?.EmpresaId || 0);
    },
    async onEmpresaChange() {
      const empresa = this.empresasActivas.find(e => Number(e.EmpresaId) === Number(this.selectedEmpresaId));
      if (!empresa) return;
      this.selectedEmpresa = empresa;
      await authService.setSelectedEmpresa(empresa);
      this.reloadFirstPage();
    },
    async bootstrap() {
      this.def = null;
      this.items = [];
      this.page = 1;
      this.searchText = '';
      this.statusFilter = '';
      try {
        if (!this.empresaId) {
          showToast('Selecciona una empresa para administrar el catálogo', 'warning');
          return;
        }
        this.def = await satCatalogoService.definicion(this.catalogo);
        if (!this.def) {
          showToast('Catálogo SAT no encontrado', 'danger');
          return;
        }
        await this.loadItems();
      } catch (e) {
        showToast('Error al cargar el catálogo SAT', 'danger');
      }
    },
    async loadItems() {
      if (!this.def) return;
      this.loading = true;
      try {
        const result = await satCatalogoService.listar({
          Catalogo: this.catalogo,
          EmpresaId: this.empresaId,
          Texto: this.searchText,
          Page: this.page,
          PageSize: this.pageSize,
          SoloActivos: this.statusFilter === 'activos'
        });
        this.items = result.items;
        this.total = result.total;
      } catch (e) {
        showToast('Error al consultar registros', 'danger');
      } finally {
        this.loading = false;
      }
    },
    reloadFirstPage() {
      this.page = 1;
      this.loadItems();
    },
    goPage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages);
      this.loadItems();
    },
    value(item, campo) {
      return item?.[campo] ?? item?.[this.toCamel(campo)] ?? item?.[campo.toLowerCase()];
    },
    rowKey(item) {
      return `${this.empresaId}-${this.value(item, this.def.LlaveCampo)}`;
    },
    toCamel(value) {
      return value ? value.charAt(0).toLowerCase() + value.slice(1) : value;
    },
    asBool(value) {
      return value === true || value === 'true' || value === 'True' || value === 1 || value === '1';
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.form = {};
      this.formFields.forEach(campo => {
        const current = item ? this.value(item, campo.Campo) : null;
        this.form[campo.Campo] = campo.Tipo === 'bool' ? this.asBool(current ?? campo.EsActivo) : (current ?? '');
      });
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.form = {};
    },
    async save() {
      this.saving = true;
      try {
        const result = await satCatalogoService.guardar({
          Catalogo: this.catalogo,
          EmpresaId: this.empresaId,
          Valores: { ...this.form }
        });
        if (result.Number >= 0) {
          showToast(result.Message || 'Registro guardado', result.Type || 'success');
          this.closeForm();
          await this.loadItems();
        } else {
          showToast(result.Message || 'No fue posible guardar', 'danger');
        }
      } catch (e) {
        showToast('Error de conexión al guardar', 'danger');
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(item) {
      const clave = this.value(item, this.def.LlaveCampo);
      if (!confirm(`¿Eliminar el registro "${clave}"?`)) return;
      try {
        const result = await satCatalogoService.borrar({
          Catalogo: this.catalogo,
          EmpresaId: this.empresaId,
          Clave: String(clave)
        });
        if (result.Number >= 0) {
          showToast(result.Message || 'Registro eliminado', result.Type || 'success');
          await this.loadItems();
        } else {
          showToast(result.Message || 'No fue posible eliminar', 'danger');
        }
      } catch (e) {
        showToast('Error de conexión al eliminar', 'danger');
      }
    },
    async copyGlobal() {
      if (!confirm('¿Copiar el catálogo global a la empresa seleccionada? Se actualizarán los registros existentes.')) return;
      this.syncing = true;
      try {
        const result = await satCatalogoService.copiarGlobalAEmpresa({ Catalogo: this.catalogo, EmpresaId: this.empresaId });
        if (result.Number >= 0) {
          showToast(result.Message || 'Catálogo copiado', result.Type || 'success');
          await this.loadItems();
        } else {
          showToast(result.Message || 'No fue posible copiar el catálogo', 'danger');
        }
      } catch (e) {
        showToast('Error de conexión al copiar el catálogo', 'danger');
      } finally {
        this.syncing = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

.sat-generic-page {
  max-width: 1240px;
}

.sat-generic-table {
  min-width: 980px;
}

.sat-form-modal {
  max-width: 860px;
}

.sat-empresa-select {
  min-width: 220px;
}

.sat-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.sat-check {
  display: flex;
  align-items: center;
  min-height: 38px;
  gap: 0.5rem;
  font-weight: 500;
}

@media (max-width: 720px) {
  .sat-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
